import React from 'react';
import { useDocumentTitle } from '../../../hooks/use-document-title';
import { useAuth } from '../../../../context/auth-provider';
import { User } from '../../../../interfaces/user-interfaces';
import { palette } from '../../../../style-utils/theme';
import { font, Text } from '../../../../style-utils/text';
import { ROLE_LABELS, ROLES, normalizeUserRole } from '../../../../auth/roles';
import { PERMISSIONS } from '../../../../auth/permissions';
import {
  DashboardHeadingContainer,
  HeadingTitleSwap,
  LogedInUser,
  ProfileButtonBorder,
  SearchAndProfileContainer,
} from '../dashboard.styled';
import { Profile } from '../../../profile/profile';
import { DISTANCE, Presence, T } from '../../../../motion';

/* The title's crossfade: the old word slips up and out, the new one rises in.
   Two pixels, the micro tempo — a change of label, not a change of screen. */
const titleEnter = { opacity: 0, y: DISTANCE.micro };
const titleCenter = { opacity: 1, y: 0 };
const titleExit = { opacity: 0, y: -DISTANCE.micro };

interface DashboardHeadingProps {
  title: string;
}
export const DashboardHeading: React.FC<DashboardHeadingProps> = ({
  title,
}) => {
  /* One call, ten dashboard screens: this component already receives the
     route's resolved title, so the tab follows the nav without ten call sites
     having to remember. */
  useDocumentTitle(title);

  const {
    user,
    permissions,
  }: {
    user: User | null | undefined;
    permissions: string[];
  } = useAuth();

  // THE DISPLAY LABEL, NEVER THE STORED VALUE. `users.role` is a snake_case
  // capability key — printing it put "property_manager" on the account chip.
  // Residency is not a role since migration 037, so a null role plus portal:read
  // is what "Resident" means here; a null role with neither is somebody whose
  // capability set is still being resolved, and that gets no line at all rather
  // than the word "Unknown".
  const staffRole = normalizeUserRole(user?.role);
  const roleText = staffRole
    ? ROLE_LABELS[staffRole]
    : permissions.includes(PERMISSIONS.PORTAL_READ)
    ? ROLE_LABELS[ROLES.RESIDENT]
    : '';

  return (
    <DashboardHeadingContainer id="dashboard-heading-container">
      {/* The page's ONE h1. `Text` is styled.p, so the dashboard's title — the
          only thing naming which of ten screens you are on — was a paragraph,
          and every dashboard screen had no heading at all. */}
      <Text
        as="h1"
        id="dashboard-title"
        fontSize={20}
        fontWeight={700}
        style={{ textTransform: 'capitalize' }}
      >
        {/* Keyed by the title so a section change swaps the text with a
            crossfade while the h1 itself — and useDocumentTitle above — stay
            exactly as they were. mode="wait": one title in the box at a time. */}
        <Presence mode="wait" initial={false}>
          <HeadingTitleSwap
            key={title}
            initial={titleEnter}
            animate={titleCenter}
            exit={titleExit}
            transition={T.micro}
          >
            {title}
          </HeadingTitleSwap>
        </Presence>
      </Text>
      <SearchAndProfileContainer id="dashboard-search-profile-container">
        <LogedInUser>
          <Text
            mt={0}
            mb={4}
            fontFamily={font.family}
            fontSize={font.size.xs}
            fontWeight={600}
            color={palette.ink}
          >
            {user?.full_name}
          </Text>
          {roleText ? (
            <Text
              mt={4}
              mb={0}
              fontFamily={font.family}
              fontSize={font.size.xxs}
              color={palette.slate}
            >
              {roleText}
            </Text>
          ) : null}
        </LogedInUser>
        <ProfileButtonBorder>
          <Profile dashboard={true} />
        </ProfileButtonBorder>
      </SearchAndProfileContainer>
    </DashboardHeadingContainer>
  );
};
